import { filter, map, sortBy, uniq } from "lodash-es";
import { observer } from "mobx-react-lite";

import { MDSIconKind } from "@/mds";
import { uuidModule } from "@/modules/uuid";
import { AgentFilterSearchField } from "@/pages/agent/components/agent-filter-search-field";
import { AgentFilterSelect } from "@/pages/agent/components/agent-filter-select";
import { AgentPageFilterBar, AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import {
  buildTaskProjectFilterItems,
  buildTaskSortFilterItems,
  buildTaskStatusFilterItems,
} from "@/pages/agent/tasks/agent-task-filter-items";
import {
  buildTaskRows,
  DEFAULT_TASK_STATUS_FILTER_VALUES,
  getSortedCompletedTaskRows,
  getSortedOpenTaskRows,
  isClosedTaskStatus,
  TaskSectionKind,
} from "@/pages/agent/tasks/agent-tasks-page.utils";
import { AgentTasksSection } from "@/pages/agent/tasks/agent-tasks-section";
import {
  ALL_PROJECTS_FILTER,
  ALL_STATUSES_FILTER,
  DEFAULT_TASK_SORT,
  useAgentTasksSearchParams,
} from "@/pages/agent/tasks/use-agent-tasks-search-params";
import { showNotImplementedAlert } from "@/pages/agent/utils/agent-not-implemented";
import { formatStatusLabel, normalizeStatusValue } from "@/pages/agent/utils/agent-status.utils";
import { useAppStore } from "@/store";
import { AssistantTaskStatus } from "@/store/assistant";
import { SubmitAssistantChangeRequestsOperation } from "@/store/sync/operations/assistant-change-requests/SubmitAssistantChangeRequestsOperation";

export const AgentTasksPage = observer(() => {
  const { store } = useAppStore();
  const { projectFilter, statusFilter, searchQuery, sortFilter, updateTaskFilters } =
    useAgentTasksSearchParams();

  const taskRows = buildTaskRows({
    tasks: store.assistantTasks.all,
    projects: store.assistantProjects.all,
    followUps: store.assistantFollowUps.all,
  });

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredTaskRows = filter(taskRows, taskRow => {
    const matchesProject =
      projectFilter === ALL_PROJECTS_FILTER || taskRow.task.projectId === projectFilter;
    const matchesStatus =
      statusFilter === ALL_STATUSES_FILTER ||
      normalizeStatusValue({ status: taskRow.task.status }) === statusFilter;
    const matchesSearch =
      !normalizedSearchQuery || taskRow.searchableText.includes(normalizedSearchQuery);

    return matchesProject && matchesStatus && matchesSearch;
  });

  const openTaskRows = getSortedOpenTaskRows({
    taskRows: filter(filteredTaskRows, taskRow => !taskRow.isClosed),
    sortKey: sortFilter,
  });
  const completedTaskRows = getSortedCompletedTaskRows({
    taskRows: filter(filteredTaskRows, taskRow => taskRow.isClosed),
    sortKey: sortFilter,
  });

  const sortFilterItems = buildTaskSortFilterItems();

  const hasProjects = store.assistantProjects.all.length > 0;
  const projectFilterItems = buildTaskProjectFilterItems({
    projectFilter,
    projectOptions: sortBy(
      map(store.assistantProjects.all, project => ({
        id: project.id,
        title: project.title,
      })),
      projectOption => projectOption.title
    ),
  });

  const statusFilterValues = sortBy(
    uniq([
      ...DEFAULT_TASK_STATUS_FILTER_VALUES,
      ...map(taskRows, taskRow => normalizeStatusValue({ status: taskRow.task.status })),
    ]),
    status => formatStatusLabel({ status })
  );
  const statusFilterItems = buildTaskStatusFilterItems({
    statusFilter,
    statusFilterValues,
  });

  const openProject = ({ projectId }: { projectId: string }) => {
    store.agentOverlay.openProject({ projectId });
  };

  const openTask = ({ taskId }: { taskId: string }) => {
    store.agentOverlay.openTask({ taskId });
  };

  const toggleTaskComplete = async ({ taskId }: { taskId: string }) => {
    const task = store.assistantTasks.get(taskId);
    if (!task) return;

    const nextStatus = isClosedTaskStatus({ status: task.status })
      ? AssistantTaskStatus.Pending
      : AssistantTaskStatus.Completed;

    await new SubmitAssistantChangeRequestsOperation({
      store,
      payload: {
        request_group_id: uuidModule.generate(),
        assistant_id: task.assistantId,
        task_change_requests: [
          {
            id: uuidModule.generate(),
            change_kind: "update",
            target_task_id: taskId,
            requested_status: nextStatus,
            changed_fields: ["status"],
          },
        ],
      },
    }).execute();
  };

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Tasks}>
      <AgentPageFilterBar>
        <AgentFilterSelect
          label="Sort"
          selectedKey={sortFilter}
          defaultKey={DEFAULT_TASK_SORT}
          items={sortFilterItems}
          leadingIcon={MDSIconKind.SortAlt}
          alwaysActive
          onSelectionChange={({ key }) => {
            updateTaskFilters({ nextSortFilter: key });
          }}
          onClear={() => {
            updateTaskFilters({ nextSortFilter: DEFAULT_TASK_SORT });
          }}
        />

        {hasProjects ? (
          <AgentFilterSelect
            label="Project"
            selectedKey={projectFilter}
            defaultKey={ALL_PROJECTS_FILTER}
            items={projectFilterItems}
            leadingIcon={MDSIconKind.Folder}
            onSelectionChange={({ key }) => {
              updateTaskFilters({ nextProjectFilter: key });
            }}
            onClear={() => {
              updateTaskFilters({ nextProjectFilter: ALL_PROJECTS_FILTER });
            }}
          />
        ) : null}

        <AgentFilterSelect
          label="Status"
          selectedKey={statusFilter}
          defaultKey={ALL_STATUSES_FILTER}
          items={statusFilterItems}
          leadingIcon={MDSIconKind.Circle}
          onSelectionChange={({ key }) => {
            updateTaskFilters({ nextStatusFilter: key });
          }}
          onClear={() => {
            updateTaskFilters({ nextStatusFilter: ALL_STATUSES_FILTER });
          }}
        />

        <AgentFilterSearchField
          ariaLabel="Filter tasks by name or project"
          placeholder="Filter by name or project"
          value={searchQuery}
          onChange={value => {
            updateTaskFilters({ nextSearchQuery: value });
          }}
        />
      </AgentPageFilterBar>

      <AgentTasksSection
        title="Open tasks"
        kind={TaskSectionKind.Open}
        taskRows={openTaskRows}
        onOpenProject={openProject}
        onOpenTask={openTask}
        onToggleTaskComplete={toggleTaskComplete}
        onCommentTask={showNotImplementedAlert}
      />

      <AgentTasksSection
        title="Completed tasks"
        kind={TaskSectionKind.Completed}
        taskRows={completedTaskRows}
        onOpenProject={openProject}
        onOpenTask={openTask}
        onToggleTaskComplete={toggleTaskComplete}
        onCommentTask={showNotImplementedAlert}
      />
    </AgentPageLayout>
  );
});
